import React from 'react';
import './TeamPlayerCard.css';
import { resolveMediaUrl } from '../../api/transport';

/**
 * Shared player card used by the daily TeamPage and the SeasonTeamPage.
 *
 * Renders: position label | avatar | name + matchup | PTS/SCORE/PROJ column.
 * Purely presentational — parent owns data fetching and click behavior.
 *
 * Season variant adds a roster-swap affordance: the position label becomes an
 * outlined button. Swap selection state is owned by the parent page and passed
 * in via `swapState`.
 */

export const getPlayerImagePath = (player) => {
    // Prefer backend-resolved presentation URL (equipped variant or default art gateway).
    // resolveMediaUrl converts the relative /api/... path to an absolute backend URL
    // in dev (where the React dev server and Django run on different ports).
    if (player.presentation?.image_url) {
        return resolveMediaUrl(player.presentation.image_url);
    }
    const gsisId = player.gsis_id || player.source_player_id;

    if (!gsisId) {
        return '/art/player_images_gsis_cartoon/default.png';
    }

    return `/art/player_images_gsis_cartoon/${gsisId}.png`;
};

export const handlePlayerImageError = (event, player) => {
    const image = event.currentTarget;
    const gsisId = player.gsis_id || player.source_player_id;
    const playerIdentity = String(player.character_sheet_id || player.id || gsisId || player.name);
    const hasPresentationImage = Boolean(player.presentation?.image_url);
    const gsisFallback = gsisId ? `/art/player_images_gsis_cartoon/${gsisId}.png` : null;
    const defaultFallback = '/art/player_images_gsis_cartoon/default.png';

    if (image.dataset.fallbackPlayer !== playerIdentity) {
        image.dataset.fallbackPlayer = playerIdentity;
        image.dataset.fallbackStage = hasPresentationImage
            ? 'presentation'
            : (gsisFallback ? 'gsis' : 'default');
    }

    if (image.dataset.fallbackStage === 'presentation') {
        image.dataset.fallbackStage = gsisFallback ? 'gsis' : 'default';
        image.src = gsisFallback || defaultFallback;
        return;
    }

    if (image.dataset.fallbackStage === 'gsis') {
        image.dataset.fallbackStage = 'default';
        image.src = defaultFallback;
        return;
    }

    image.dataset.fallbackStage = 'stopped';
    image.onerror = null;
};

export const formatGameInfo = (player) => {
    // Use actual matchup data from backend
    if (player.matchup_display) {
        return player.matchup_display;
    }

    // Fallback: construct from team/opponent if matchup_display not available
    if (player.team && player.opponent_team) {
        return `${player.team} vs ${player.opponent_team}`;
    }

    // Final fallback: show just the team if available
    if (player.team) {
        return player.team;
    }

    // No data available
    return 'TBD';
};

const VALID_SWAP_STATES = ['idle', 'selected', 'eligible', 'ineligible'];

function TeamPlayerCard({
    player,
    onClick,
    variant = 'daily',
    swapEnabled = false,
    swapState = 'idle',
    onPositionClick,
}) {
    if (!player) {
        throw new Error('❌ TeamPlayerCard: player prop is required');
    }

    if (!VALID_SWAP_STATES.includes(swapState)) {
        throw new Error(
            `❌ TeamPlayerCard: invalid swapState '${swapState}' for player ${player.name} — must be one of ${VALID_SWAP_STATES.join(', ')}`
        );
    }

    const handleClick = () => {
        if (onClick) {
            onClick(player);
        }
    };

    const handlePositionClick = (event) => {
        if (!swapEnabled || !onPositionClick) {
            return;
        }
        // Position box owns swap selection — don't also trigger the card's
        // navigate-to-player click.
        event.stopPropagation();
        onPositionClick(player);
    };

    const cardClasses = [
        'player-card',
        onClick ? 'player-card--clickable' : '',
        variant === 'season' ? 'player-card--season' : '',
        swapEnabled ? `player-card--swap-${swapState}` : ''
    ].filter(Boolean).join(' ');

    const positionClasses = [
        'player-position',
        swapEnabled ? 'player-position--swappable' : ''
    ].filter(Boolean).join(' ');

    return (
        <div
            className={cardClasses}
            onClick={handleClick}
        >
            <div className="player-card-left">
                {swapEnabled ? (
                    <button
                        type="button"
                        className={positionClasses}
                        onClick={handlePositionClick}
                        aria-pressed={swapState === 'selected'}
                        aria-label={`Swap ${player.name} (${player.position})`}
                        title={`Swap ${player.position}`}
                    >
                        {player.position}
                    </button>
                ) : (
                    <div className={positionClasses}>{player.position}</div>
                )}
                <img
                    src={getPlayerImagePath(player)}
                    onError={(event) => handlePlayerImageError(event, player)}
                    alt={player.name}
                    className="player-card-image"
                />
                <div className="player-card-info">
                    <div className="player-card-name">{player.name}</div>
                    <div className="player-card-game">{formatGameInfo(player)}</div>
                </div>
            </div>
            {/*
              * SCORE / PROJ obey the three-state number rule used everywhere
              * else in this codebase (see the "Money-facing display contract"
              * in AIDocs/Final/frontend/component-reference-tdd.md — the same
              * reasoning applies to any number a user acts on, not just money):
              *
              *   real number  → the number, including a genuine 0.0
              *   missing      → an explicit '—'
              *
              * BOTH lines previously tested TRUTHINESS, and both were wrong in
              * opposite directions:
              *   · SCORE `player.score ? … : '—'` — a player who genuinely
              *     scored 0.0 was shown as "unknown", hiding a real result.
              *   · PROJ  `… : '0.0'` — a MISSING projection was shown as a
              *     confident 0.0. Season roster rows carry no projection at
              *     all, so every season player was rendered as "projected to
              *     score nothing" — a fabricated number a manager could
              *     reasonably bench someone over.
              *
              * `typeof === 'number'` is what distinguishes the two; `0` is
              * falsy, so truthiness can never tell "zero" from "absent".
              */}
            <div className="player-card-right">
                <div className="player-card-pts-label">PTS</div>
                <div className="player-card-score-row">
                    <span className="player-card-score-label">SCORE</span>
                    <span className="player-card-score-value">
                        {typeof player.score === 'number' ? player.score.toFixed(1) : '—'}
                    </span>
                </div>
                <div className="player-card-proj-row">
                    <span className="player-card-proj-label">PROJ</span>
                    <span className="player-card-proj-value">
                        {typeof player.projected_points === 'number'
                            ? player.projected_points.toFixed(1)
                            : '—'}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default TeamPlayerCard;
